import { useEffect, useState } from 'react';
import OpenSeadragon from 'openseadragon';
import { AT2_SLIDE_SCANNER_NAME } from 'components/ImageViewer/constants';
import {
  COLOR_PROFILES,
  getColorProfileLut,
  resolveColorProfile,
} from 'components/ImageViewer/colorProfiles/colorProfiles';
import { createColorProfileFilter } from 'components/ImageViewer/colorProfiles/colorProfileFilter';

export const GT450_SCANNER_FILTERS_SETTINGS = {
  gamma: OpenSeadragon.Filters.GAMMA(0.5),
};

const COLOR_PROFILE_FILTER_KEY = 'colorProfile';

export const useViewerFilters = (
  viewer,
  isGT450ScannerFiltersEnabled,
  slide,
  savedColorProfile,
) => {
  const [usedFilters, setUsedFilters] = useState({});
  const colorProfile = resolveColorProfile(savedColorProfile);
  const isAT2Slide = slide?.scanner_type === AT2_SLIDE_SCANNER_NAME;

  // removing filters
  useEffect(() => {
    if (!isGT450ScannerFiltersEnabled && isAT2Slide) {
      setUsedFilters((prevState) => {
        const newFilters = Object.entries(prevState).reduce(
          (filters, filter) => ({
            ...filters,
            ...(!(filter[0] in GT450_SCANNER_FILTERS_SETTINGS) && {
              [filter[0]]: filter[1],
            }),
          }),
          {},
        );

        return newFilters;
      });
    }
  }, [isGT450ScannerFiltersEnabled, isAT2Slide]);

  // setting filters
  useEffect(() => {
    if (isGT450ScannerFiltersEnabled && isAT2Slide) {
      setUsedFilters((prevState) => ({
        ...prevState,
        ...GT450_SCANNER_FILTERS_SETTINGS,
      }));
    }
  }, [isGT450ScannerFiltersEnabled, isAT2Slide]);

  // setting / removing the color-profile filter. The LUT is loaded lazily and
  // only when a non-Original profile is selected.
  useEffect(() => {
    if (colorProfile === COLOR_PROFILES.ORIGINAL) {
      setUsedFilters((prevState) => {
        if (!(COLOR_PROFILE_FILTER_KEY in prevState)) {
          return prevState;
        }

        const nextFilters = { ...prevState };
        delete nextFilters[COLOR_PROFILE_FILTER_KEY];

        return nextFilters;
      });

      return undefined;
    }

    let cancelled = false;

    getColorProfileLut(colorProfile)
      .then((lut) => createColorProfileFilter(lut))
      .then((processor) => {
        if (cancelled) {
          return;
        }

        setUsedFilters((prevState) => ({
          ...prevState,
          [COLOR_PROFILE_FILTER_KEY]: processor,
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [colorProfile]);

  // The viewer is rebuilt whenever the slide (or navigator, annotations, ...)
  // changes, so re-apply the active filters to every new instance, not only
  // when the filters themselves change.
  useEffect(() => {
    const filters = Object.values(usedFilters);

    viewer?.setFilterOptions({
      filters: {
        processors: filters,
      },
    });
  }, [viewer, usedFilters]);
};
